import { useEffect, useState } from "react";
import { doc, onSnapshot, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";

/**
 * Suscribe a appConfig/runtime y, si reloadAt > BUILD_TIMESTAMP_MS,
 * muestra un banner persistente y recarga la página.
 *
 * Para forzar reload de TODOS los clientes activos:
 *   node scripts/force-reload.mjs <dev|prod>
 *
 * Plan B para emergencias durante eventos en vivo.
 */
const HANDLED_KEY = "reloadWatcher:lastHandledMs";

function getHandledMs(): number {
  try {
    const v = localStorage.getItem(HANDLED_KEY);
    return v ? Number(v) || 0 : 0;
  } catch {
    return 0;
  }
}
function setHandledMs(ms: number) {
  try {
    localStorage.setItem(HANDLED_KEY, String(ms));
  } catch {
    /* noop */
  }
}

export default function ReloadWatcher() {
  const [show, setShow] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(10);

  useEffect(() => {
    const ref = doc(db, "appConfig", "runtime");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data() as { reloadAt?: Timestamp };
        const reloadAtMs = data.reloadAt?.toMillis?.() ?? 0;
        if (!reloadAtMs) return;
        // Guard: si ya procesamos este reloadAt en una recarga anterior,
        // no volvemos a disparar (evita bucle infinito si el build
        // desplegado tiene timestamp anterior al reloadAt).
        const handled = getHandledMs();
        if (
          reloadAtMs > __BUILD_TIMESTAMP_MS__ &&
          reloadAtMs > handled
        ) {
          setHandledMs(reloadAtMs);
          setShow(true);
        }
      },
      (err) => {
        // Silencioso: si no hay permisos o el doc no existe, no rompemos la app
        console.warn("[ReloadWatcher] no se pudo suscribir:", err.message);
      },
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!show) return;
    if (secondsLeft <= 0) {
      window.location.reload();
      return;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [show, secondsLeft]);

  if (!show) return null;

  return (
    <div
      role="alert"
      className="fixed top-0 inset-x-0 z-[9999] bg-amber-500 text-white shadow-lg"
    >
      <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3 text-sm sm:text-base">
        <div>
          <strong>Hay una nueva versión disponible.</strong>{" "}
          Recargando en {secondsLeft}s…
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="bg-white text-amber-700 font-semibold px-3 py-1.5 rounded hover:bg-amber-50"
        >
          Recargar ahora
        </button>
      </div>
    </div>
  );
}
